import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export interface LoanEligibility {
  id: number;
  loan_type: string;
  eligible_amount: number;
  requires_guarantors: boolean;
  guarantor_details?: {
    threshold_type: string;
    threshold_value: number;
    threshold_amount: number;
    explanation: string;
  };
  member_savings: number;
  eligibility_breakdown: {
    type: string;
    savings?: number;
    multiplier?: number;
    fixed_amount?: number;
    calculation: string;
  };
}

export interface GuarantorCalculation {
  loan_amount: number;
  guarantor_amount: number;
  requires_guarantors: boolean;
  explanation: string;
}

export const useLoanEligibility = () => {
  return useQuery({
    queryKey: ['loan-eligibility'],
    queryFn: async (): Promise<LoanEligibility[]> => {
      const response = await api.get('/loan-eligibility');
      return response.data.data;
    },
  });
};

export const useLoanTypeEligibility = (loanTypeId: number) => {
  return useQuery({
    queryKey: ['loan-eligibility', loanTypeId],
    queryFn: async (): Promise<LoanEligibility> => {
      const response = await api.get(`/loan-eligibility/${loanTypeId}/detailed`);
      return response.data.data;
    },
    enabled: !!loanTypeId,
  });
};

export const useCalculateGuarantorAmount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ loanTypeId, loanAmount }: { loanTypeId: number; loanAmount: number }): Promise<GuarantorCalculation> => {
      const response = await api.post(`/loan-eligibility/${loanTypeId}/calculate-guarantor`, {
        loan_amount: loanAmount,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-eligibility'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
}; 